import { Controller } from "@hotwired/stimulus"

// Enregistrement audio d'une question via l'API MediaRecorder du navigateur.
// - Compatible Safari iOS (audio/mp4), Chrome/Firefox (audio/webm).
// - Le blob est déposé dans un <input type="file"> caché, soumis avec le form.
// - Si le microphone est indisponible ou refusé, le formulaire texte reste utilisable.
export default class extends Controller {
  static targets = [
    "button",
    "buttonLabel",
    "fileInput",
    "recordingPanel",
    "playbackPanel",
    "timer",
    "audio",
    "error"
  ]

  static values = {
    maxSeconds: { type: Number, default: 180 }
  }

  connect() {
    this.state = "idle"
    this.chunks = []
    this.stream = null
    this.mediaRecorder = null
    this.timerId = null
    this.startedAt = 0
    this.audioUrl = null

    if (!this.isSupported) {
      this.buttonTarget.disabled = true
      this.buttonTarget.title = "Enregistrement vocal non pris en charge par ce navigateur"
      this.buttonTarget.classList.add("opacity-40", "cursor-not-allowed")
    }
  }

  disconnect() {
    this.releaseStream()
    if (this.timerId) clearInterval(this.timerId)
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl)
  }

  get isSupported() {
    return !!(
      typeof window !== "undefined" &&
      navigator &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      typeof window.MediaRecorder === "function"
    )
  }

  pickMimeType() {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/aac",
      "audio/ogg;codecs=opus"
    ]
    if (!window.MediaRecorder || typeof MediaRecorder.isTypeSupported !== "function") return ""
    for (const t of candidates) {
      if (MediaRecorder.isTypeSupported(t)) return t
    }
    return ""
  }

  async toggle(event) {
    event.preventDefault()
    if (!this.isSupported) return
    if (this.state === "recording") return this.stop()
    return this.start()
  }

  async start() {
    this.clearError()
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      const msg = (e && (e.name === "NotAllowedError" || e.name === "SecurityError"))
        ? "Accès au microphone refusé. Vous pouvez continuer à écrire votre question."
        : (e && e.name === "NotFoundError")
          ? "Aucun microphone détecté. Vous pouvez continuer à écrire votre question."
          : "Microphone indisponible. Vous pouvez continuer à écrire votre question."
      return this.showError(msg)
    }

    const mime = this.pickMimeType()
    try {
      this.mediaRecorder = mime
        ? new MediaRecorder(this.stream, { mimeType: mime })
        : new MediaRecorder(this.stream)
    } catch (_e) {
      this.releaseStream()
      return this.showError("Impossible de démarrer l'enregistrement sur ce navigateur.")
    }

    this.chunks = []
    this.mediaRecorder.addEventListener("dataavailable", (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data)
    })
    this.mediaRecorder.addEventListener("stop", () => this.finalize())
    this.mediaRecorder.addEventListener("error", () => {
      this.showError("Une erreur est survenue pendant l'enregistrement.")
      this.releaseStream()
      this.setState("idle")
    })

    try {
      this.mediaRecorder.start()
    } catch (_e) {
      this.releaseStream()
      return this.showError("Impossible de démarrer l'enregistrement sur ce navigateur.")
    }

    this.startedAt = Date.now()
    this.setState("recording")
    this.updateTimer()
    this.timerId = setInterval(() => this.updateTimer(), 250)
  }

  stop(event) {
    if (event) event.preventDefault()
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      try { this.mediaRecorder.stop() } catch (_e) {}
    }
  }

  finalize() {
    if (this.timerId) { clearInterval(this.timerId); this.timerId = null }
    this.releaseStream()

    const mime = (this.mediaRecorder && this.mediaRecorder.mimeType) || "audio/webm"
    const blob = new Blob(this.chunks, { type: mime })
    const ext = mime.includes("mp4") || mime.includes("aac") ? "m4a"
              : mime.includes("ogg") ? "ogg"
              : "webm"
    const file = new File([blob], `question-${Date.now()}.${ext}`, { type: mime })

    try {
      const dt = new DataTransfer()
      dt.items.add(file)
      this.fileInputTarget.files = dt.files
    } catch (_e) {
      // Certains navigateurs restreignent DataTransfer.files ; le champ reste vide,
      // mais on garde le lecteur pour permettre à l'utilisateur d'écouter et réessayer.
    }

    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl)
    this.audioUrl = URL.createObjectURL(blob)
    this.audioTarget.src = this.audioUrl

    this.setState("recorded")
  }

  delete(event) {
    if (event) event.preventDefault()
    if (this.audioUrl) { URL.revokeObjectURL(this.audioUrl); this.audioUrl = null }
    try { this.audioTarget.pause() } catch (_e) {}
    this.audioTarget.removeAttribute("src")
    try { this.audioTarget.load() } catch (_e) {}
    this.fileInputTarget.value = ""
    this.chunks = []
    this.setState("idle")
  }

  async restart(event) {
    if (event) event.preventDefault()
    this.delete()
    await this.start()
  }

  updateTimer() {
    const seconds = Math.floor((Date.now() - this.startedAt) / 1000)
    if (this.hasTimerTarget) this.timerTarget.textContent = this.formatDuration(seconds)
    if (seconds >= this.maxSecondsValue) this.stop()
  }

  formatDuration(s) {
    const m = String(Math.floor(s / 60)).padStart(2, "0")
    const r = String(s % 60).padStart(2, "0")
    return `${m}:${r}`
  }

  releaseStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => { try { t.stop() } catch (_e) {} })
      this.stream = null
    }
  }

  showError(msg) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  setState(state) {
    this.state = state
    const isRecording = state === "recording"
    const isRecorded  = state === "recorded"

    if (this.hasRecordingPanelTarget) this.recordingPanelTarget.classList.toggle("hidden", !isRecording)
    if (this.hasPlaybackPanelTarget)  this.playbackPanelTarget.classList.toggle("hidden", !isRecorded)

    // Le bouton micro dans le textarea est masqué pendant l'enregistrement et une fois enregistré,
    // les actions passent alors par les panneaux dédiés.
    this.buttonTarget.classList.toggle("hidden", isRecording || isRecorded)

    if (isRecording) {
      this.buttonTarget.setAttribute("aria-label", "Arrêter l'enregistrement")
    } else {
      this.buttonTarget.setAttribute("aria-label", "Enregistrer ma question au microphone")
    }
  }
}
